import { styled } from '@linaria/react';
import { Controls, Edge, EdgeTypes, Node, NodeTypes, Position, ReactFlow, useReactFlow } from '@xyflow/react';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResolvedPhaseLabels } from '../../../helpers/phase-labels/phase-labels.type';
import { primaryBase, redBase, secondaryBase, skyDark } from '../../../styles/design-tokens';
import type { EventScope } from '../context/process.context.type';
import { ProcessGraphLegend } from '../process-graph-legend';
import type { ViewType } from '../process.type';
import { BackwardsEventEdge } from './backwards-event-edge';
import { EdgeLabelNode } from './edge-label-node';
import { EventEdge } from './event-edge';
import type { EventGraph } from './event-graph-client.type';
import { calculateBackwardsEdge, getFilteredEdgesAndNodes } from './event-graph.helpers';
import { EventNode } from './event-node';

// Node and edge type definitions
const nodeTypes: NodeTypes = {
  eventNode: EventNode,
  edgeLabel: EdgeLabelNode,
};

const edgeTypes: EdgeTypes = {
  weightedEdge: EventEdge,
  backwardsEdge: BackwardsEventEdge,
};

type Props = {
  eventGraph: EventGraph | undefined;
  filterLevel: number;
  viewType: ViewType;
  eventScope: EventScope;
  isRightGraph: boolean;
  /**
   * Optional per-workflow display labels sourced from the
   * workflow-scoped `external_section_map` response. Forwarded into
   * `ProcessGraphLegend` so the "Waiting" overview metric renders the
   * override label.
   */
  resolvedLabels?: ResolvedPhaseLabels;
};

export const SectionEventGraph = memo(
  ({ eventGraph, filterLevel, viewType, eventScope, isRightGraph, resolvedLabels }: Props) => {
    const { t } = useTranslation();
    const reactFlowInstance = useReactFlow();

    // Fit view when nodes are loaded
    useEffect(() => {
      if (eventGraph?.nodes?.length) {
        setTimeout(() => {
          reactFlowInstance.fitView({ padding: 0.3 }); // Increased padding for better view
        }, 100);
      }
    }, [eventGraph, reactFlowInstance]);

    // Transform nodes into React Flow format and position them in a grid by sequence
    const { nodes, edges } = useMemo(() => {
      if (!eventGraph) {
        return { nodes: [], edges: [] };
      }

      const { filteredEdges, includedNodes } = getFilteredEdgesAndNodes(eventGraph, filterLevel);

      // Create React Flow nodes with positions based on sequence
      const rowWidth = 300; // Width for horizontal spacing between nodes in same sequence
      const columnHeight = 250; // Height for vertical spacing between sequences

      // Filter nodes to only include those in the includedNodes set
      const filteredNodes = eventGraph.nodes.filter((node) => includedNodes.has(node.id));

      // Group nodes by sequence and type
      const nodesBySequence: {
        [key: number]: {
          exitStatusNodes: typeof filteredNodes;
          regularStatusNodes: typeof filteredNodes;
          otherNodes: typeof filteredNodes;
        };
      } = {};

      // Initialize sequences
      const sequences = [...new Set(filteredNodes.map((node) => node.sequence))];
      sequences.forEach((sequence) => {
        nodesBySequence[sequence] = {
          exitStatusNodes: [],
          regularStatusNodes: [],
          otherNodes: [],
        };
      });

      // Group nodes by sequence and type
      filteredNodes.forEach((node) => {
        const sequence = node.sequence;
        const maybeStatus = node.id.split('_')?.[1]?.toLowerCase();

        if (node.event_type === 'status') {
          // Check if this is an exit status (not in eventScope.statuses)
          if (!eventScope.statuses.map((s) => s.toLowerCase()).includes(maybeStatus)) {
            nodesBySequence[sequence].exitStatusNodes.push(node);
          } else {
            nodesBySequence[sequence].regularStatusNodes.push(node);
          }
        } else {
          nodesBySequence[sequence].otherNodes.push(node);
        }
      });

      // Create React Flow nodes with custom positioning
      const rfNodes: Node<{
        id: string;
        event_type: string;
        sequence: number;
        isRightGraph: boolean;
        index?: number;
        task_count?: number;
        onLayout?: () => void;
        isExitNode?: boolean;
      }>[] = [];

      // Process each sequence
      Object.entries(nodesBySequence).forEach(([sequenceStr, { exitStatusNodes, regularStatusNodes, otherNodes }]) => {
        const sequence = parseInt(sequenceStr, 10);

        // Special handling for sequence 0 - keep centered layout
        if (sequence === 0) {
          // For sequence 0, maintain current centered layout
          const allNodesInSequence = [...exitStatusNodes, ...regularStatusNodes, ...otherNodes];
          const totalWidth = allNodesInSequence.length * rowWidth;
          const startX = -(totalWidth / 2) + rowWidth / 2; // Calculate start X for centering

          allNodesInSequence.forEach((node, index) => {
            rfNodes.push({
              id: node.id,
              type: 'eventNode',
              position: {
                x: startX + index * rowWidth,
                y: sequence * columnHeight + 100,
              },
              data: {
                id: node.id,
                event_type: node.event_type,
                sequence: node.sequence,
                isRightGraph: isRightGraph,
              },
              sourcePosition: Position.Bottom,
              targetPosition: Position.Top,
            });
          });
        } else {
          // For other sequences, position nodes in three sections

          // Left section - Exit status nodes
          const exitStartX = -((regularStatusNodes.length * rowWidth) / 2) - exitStatusNodes.length * rowWidth - 100;
          exitStatusNodes.forEach((node, index) => {
            rfNodes.push({
              id: node.id,
              type: 'eventNode',
              position: {
                x: exitStartX + index * rowWidth,
                y: sequence * columnHeight + 100,
              },
              data: {
                id: node.id,
                event_type: node.event_type,
                sequence: node.sequence,
                isRightGraph: isRightGraph,
                isExitNode: true,
              },
              sourcePosition: Position.Bottom,
              targetPosition: Position.Top,
            });
          });

          // Center section - Regular status nodes
          const regularStartX = -(regularStatusNodes.length * rowWidth) / 2 + rowWidth / 2;
          regularStatusNodes.forEach((node, index) => {
            rfNodes.push({
              id: node.id,
              type: 'eventNode',
              position: {
                x: regularStartX + index * rowWidth,
                y: sequence * columnHeight + 100,
              },
              data: {
                id: node.id,
                event_type: node.event_type,
                sequence: node.sequence,
                isRightGraph: isRightGraph,
              },
              sourcePosition: Position.Bottom,
              targetPosition: Position.Top,
            });
          });

          // Right section - Other nodes
          const otherStartX = (regularStatusNodes.length * rowWidth) / 2 + 100;
          otherNodes.forEach((node, index) => {
            rfNodes.push({
              id: node.id,
              type: 'eventNode',
              position: {
                x: otherStartX + index * rowWidth,
                y: sequence * columnHeight + 100,
              },
              data: {
                id: node.id,
                event_type: node.event_type,
                sequence: node.sequence,
                isRightGraph: isRightGraph,
              },
              sourcePosition: Position.Bottom,
              targetPosition: Position.Top,
            });
          });
        }
      });

      // Create React Flow edges with weight data (only for filtered edges)
      const rfEdges: Edge<{
        weight: number;
        task_count: number;
        cycle_time: number;
        percentage: number;
        cost: number;
        viewType: string;
        dashed: boolean;
        isRightGraph: boolean;
      }>[] = [];

      filteredEdges.forEach((edge) => {
        // Get source and target node objects to check their sequences
        const sourceNode = filteredNodes.find((node) => node.id === edge.source);
        const targetNode = filteredNodes.find((node) => node.id === edge.target);

        // Check if this edge traverses back to sequence 0
        const isBackTraversal =
          sourceNode && targetNode && sourceNode.sequence > targetNode.sequence && targetNode.sequence === 0;

        if (isBackTraversal) {
          const { newEdgeLabelNode, newEdges } = calculateBackwardsEdge(
            edge,
            sourceNode,
            targetNode,
            rfNodes as any,
            viewType,
          );
          if (newEdgeLabelNode) {
            rfNodes.push(newEdgeLabelNode as any);
          }
          rfEdges.push(...(newEdges as any));
        } else {
          rfEdges.push({
            id: `${edge.source}-${edge.target}`,
            source: edge.source,
            target: edge.target,
            // Specify the exact handles to connect to
            sourceHandle: `${edge.source}-source`,
            targetHandle: `${edge.target}-target`,
            type: 'weightedEdge',
            data: {
              weight: edge.weight,
              task_count: edge.task_count,
              cycle_time: edge.cycle_time,
              percentage: edge.percentage,
              cost: edge.cost,
              viewType: viewType,
              dashed: isBackTraversal || false, // Set dashed property based on if edge goes back to sequence 0
              isRightGraph: isRightGraph,
            },
            animated: true, // Add animation for better visibility
            style: {
              stroke: isBackTraversal ? skyDark : secondaryBase, // Use purple color for back-traversal edges
            },
          });
        }
      });

      return { nodes: rfNodes, edges: rfEdges };
    }, [eventGraph, filterLevel, viewType, eventScope.statuses, isRightGraph]);

    // Configure React Flow props
    const flowProps = useMemo(
      () => ({
        nodeOrigin: [0.5, 0.5] as [number, number],
        fitView: true,
        nodeTypes,
        edgeTypes,
        nodes,
        edges,
        proOptions: { hideAttribution: true },
        edgesFocusable: true,
        elementsSelectable: true,
        minZoom: 0.1,
        maxZoom: 2,
        defaultEdgeOptions: {
          type: 'weightedEdge',
          animated: true,
        },
      }),
      [nodes, edges],
    );

    return (
      <StyledReactFlow {...flowProps}>
        <Controls position="bottom-right" showInteractive={false} />
        <ProcessGraphLegend
          legendItems={[
            { icon: 'login', color: primaryBase, text: `Entered ${t('common.status.status')} Group` },
            { icon: 'logout', color: redBase, text: `Exited ${t('common.status.status')} Group` },
            { icon: 'crop_square', color: secondaryBase, text: `Other ${t('process.event_graph.event_plural')}` },
            { icon: 'horizontal_rule', color: secondaryBase, text: `${t('process.event_graph.event')} Path` },
            { icon: 'more_horiz', color: skyDark, text: 'Backwards Path' },
          ]}
          resolvedLabels={resolvedLabels}
        />
      </StyledReactFlow>
    );
  },
);

const StyledReactFlow = styled(ReactFlow)`
  .react-flow__handle {
    opacity: 0;
    border: none;
    min-height: 0;
  }

  .react-flow__controls {
    background: #f5f5f5;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.3);
  }

  .react-flow__edge-path {
    stroke: #000;
  }
`;
